import React, { useState, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { ManualEditsRow } from "./ManualEditsRow";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";

const roleOptions = [
  { value: "all", label: "All" },
  { value: "player", label: "Player" },
  { value: "coach", label: "Coach" },
  { value: "scout", label: "Scout" },
];

export const ManualEditsTable = ({ rows, onRowClick, loading, pagination, onLoadMore, roleFilter, onRoleChange, onSearchChange }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  // Debounce search and call API
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(search);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleViewProfile = (row) => {
    const role = row.role.toLowerCase();
    if (role === 'player') {
      navigate(`/admin/player/${row.id}`);
    } else if (role === 'coach') {
      navigate(`/admin/coach/${row.id}`);
    } else if (role === 'scout') {
      navigate(`/admin/scout/${row.id}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search + Role filter */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
        <div className="relative w-full md:max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted">
            <FiSearch size={16} />
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="w-full h-9 pl-9 pr-8 rounded-lg bg-jp-card border border-jp-line text-xs text-jp-ink placeholder:text-jp-muted focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2 text-[11px] text-jp-ink">
          <span>Role</span>
          <SimpleDropdown
            value={roleFilter}
            onChange={onRoleChange}
            options={roleOptions}
            placeholder="All"
            variant="admin"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-jp-card border border-jp-line overflow-hidden flex-1 flex flex-col max-h-[600px]">
        <div className="overflow-y-auto flex-1">
          <table className="min-w-full text-[11px] border-collapse">
            <thead className="bg-jp-card text-jp-ink sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-center bg-jp-card">NAME</th>
                <th className="px-4 py-2 text-center bg-jp-card">TEAM / SCHOOL</th>
                <th className="px-4 py-2 text-center bg-jp-card">ROLE</th>
                <th className="px-4 py-2 text-center bg-jp-card">LAST UPDATE</th>
                <th className="px-4 py-2 text-center bg-jp-card">STATUS</th>
                {roleFilter !== 'coach' && roleFilter !== 'scout' && (
                  <th className="px-4 py-2 text-center bg-jp-card">COMMITMENT STATUS</th>
                )}
                <th className="px-4 py-2 text-center bg-jp-card">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-jp-muted">
                    {search ? 'No matching users found' : 'No users found'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <ManualEditsRow
                    key={row.id}
                    row={row}
                    onClick={() => onRowClick(row)}
                    onViewProfile={handleViewProfile}
                    roleFilter={roleFilter}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More Button */}
        {pagination?.hasMore && (
          <div className="px-6 py-4 border-t border-jp-line flex justify-center">
            <button
              onClick={onLoadMore}
              disabled={loading}
              className="px-6 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  Load More
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}

        {/* Showing count */}
        {pagination && (
          <div className="px-6 py-3 border-t border-jp-line text-center">
            <p className="text-xs text-jp-muted">
              Showing {rows.length} of {pagination.totalCount} users
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
